import React, { useCallback, useEffect, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from '../utils/apiError';

/**
 * FREE ISSUE — the material WE supply for a component a vendor makes for us.
 *
 * Without a row here, a purchased component is what it looks like: we buy a finished part
 * and pay the vendor's price. With one, the arrangement is the other one — we drive our own
 * stock over, the vendor converts it, and their invoice is labour only. The link is what
 * makes the rest of the chain work:
 *
 *   - the component's cost becomes material + conversion instead of the vendor's price alone;
 *   - the nightly reconciler reserves the sticks the outstanding component demand will need,
 *     so shop jobs cannot quietly eat steel that is already spoken for;
 *   - receiving the components draws the vendor's custody down by what they consumed;
 *   - drops come back to stock as remnants when the vendor returns them.
 *
 * `pieces_per_stock_length` is the whole conversion. It is a COUNT of finished pieces from one
 * purchased stock length, not a length — 4 brackets from a 24 ft stick is 4, whatever the stick
 * measures. Everything downstream divides by it and rounds UP: a single piece burns a whole
 * stick, because it did.
 */
export default function FreeIssueYieldCard({ consumableId, productId, onChanged }) {
  const API_BASE = config.API_BASE_URL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [materials, setMaterials] = useState([]);
  const [materialSearch, setMaterialSearch] = useState("");
  const blank = {
    material_id: "", pieces_per_stock_length: "", piece_length: "",
    usable_stock_length: "", returns_drops: false, expected_drop_length: "", notes: "",
  };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    if (!consumableId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/consumables/${consumableId}/material_yield`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setRows(await res.json());
    } catch (err) {
      setError(`Could not load the free-issue link: ${err.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, consumableId]);

  useEffect(() => { load(); }, [load]);

  // Materials are only needed once the operator opens the form — a stock list on every
  // component render would be a request per selection for a card most components never use.
  const loadMaterials = useCallback(async () => {
    if (materials.length) return;
    try {
      const res = await fetch(`${API_BASE}/materials`);
      if (res.ok) setMaterials(await res.json());
    } catch { /* the select degrades to empty; the refusal below still explains why */ }
  }, [API_BASE, materials.length]);

  const startAdd = () => { setForm(blank); setEditingId(null); setAdding(true); loadMaterials(); };
  const startEdit = (r) => {
    setForm({
      material_id: r.material_id,
      pieces_per_stock_length: r.pieces_per_stock_length ?? "",
      piece_length: r.piece_length ?? "",
      usable_stock_length: r.usable_stock_length ?? "",
      returns_drops: !!r.returns_drops,
      expected_drop_length: r.expected_drop_length ?? "",
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setAdding(true);
    loadMaterials();
  };
  const cancel = () => { setAdding(false); setEditingId(null); setForm(blank); setError(""); };

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      // The server owns the rules (positive count, drop length vs stock length, one row per
      // material). Sending the form as-is and surfacing its refusal keeps ONE definition of
      // valid rather than a second, drifting copy in the browser.
      const body = {
        pieces_per_stock_length: num(form.pieces_per_stock_length),
        piece_length: num(form.piece_length),
        usable_stock_length: num(form.usable_stock_length),
        returns_drops: !!form.returns_drops,
        expected_drop_length: form.returns_drops ? num(form.expected_drop_length) : null,
        notes: form.notes || null,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API_BASE}/consumables/${consumableId}/material_yield/${editingId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_BASE}/consumables/${consumableId}/material_yield`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...body, material_id: num(form.material_id) }),
        });
      }
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      cancel();
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    // Deleting the link does NOT unwind history: custody already drawn down stays drawn down,
    // and remnants already credited stay credited. It only stops FUTURE receipts from
    // consuming the vendor's position, which is why the confirm says so out loud.
    if (!window.confirm(
      `Remove the free-issue link between ${productId || "this component"} and `
      + `${r.material_code || r.material_description || "this material"}?\n\n`
      + `Future receipts will stop drawing the vendor's custody down and the component's cost `
      + `reverts to the purchase price. Custody already consumed is not restored.`
    )) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/consumables/${consumableId}/material_yield/${r.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!consumableId) return null;

  const money = (v) => (v === null || v === undefined || v === "" ? "—" : `$${Number(v).toFixed(2)}`);
  const per = (r) => Number(r.pieces_per_stock_length || 0);
  const perPieceMaterial = (r) => {
    const c = Number(r.material_cost_per_stock_unit || 0);
    return per(r) > 0 && c > 0 ? c / per(r) : null;
  };

  return (
    <div className="card mb-3 border-success">
      <div className="card-header bg-success bg-opacity-10 d-flex justify-content-between align-items-center">
        <span><strong>Free Issue</strong> <span className="text-muted small">— material we supply for this component</span></span>
        {!adding && (
          <button className="btn btn-sm btn-outline-success" onClick={startAdd} disabled={busy}>
            + Link material
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {loading && <div className="text-muted small">Loading…</div>}

        {!loading && !rows.length && !adding && (
          <div className="text-muted small mb-0">
            No material linked — this component is bought finished, at the vendor's price.
            Link a material if we supply the stock and the vendor invoices conversion only.
          </div>
        )}

        {!!rows.length && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-2">
              <thead>
                <tr>
                  <th>Material</th>
                  <th className="text-end">Pieces / stock length</th>
                  <th className="text-end">Material $ / piece</th>
                  <th>Drops</th>
                  <th className="text-end"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div><strong>{r.material_code || `#${r.material_id}`}</strong></div>
                      <div className="text-muted small">{r.material_description}</div>
                      <div className="text-muted small">
                        On hand {Number(r.stock_quantity ?? 0)} · reserved {Number(r.reserved_quantity ?? 0)}
                        {r.stock_length ? ` · ${r.stock_length}" sticks` : ""}
                      </div>
                    </td>
                    <td className="text-end">
                      <span className="fs-5">{r.pieces_per_stock_length}</span>
                      {r.piece_length ? (
                        <div className="text-muted small">{r.piece_length}" each</div>
                      ) : null}
                    </td>
                    <td className="text-end">
                      {money(perPieceMaterial(r))}
                      <div className="text-muted small">
                        {money(r.material_cost_per_stock_unit)}/stick
                        {r.material_vendor_name ? ` · ${r.material_vendor_name}` : ""}
                      </div>
                    </td>
                    <td>
                      {r.returns_drops ? (
                        <span className="badge bg-success">
                          Returned{r.expected_drop_length ? ` · ${r.expected_drop_length}"` : ""}
                        </span>
                      ) : (
                        <span className="badge bg-secondary">Vendor keeps</span>
                      )}
                    </td>
                    <td className="text-end text-nowrap">
                      <button className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => startEdit(r)} disabled={busy}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger"
                              onClick={() => remove(r)} disabled={busy}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {adding && (
          <div className="border rounded p-3 bg-light">
            <div className="row g-2">
              {!editingId && (
                <div className="col-12">
                  <label className="form-label small mb-1">Material we supply *</label>
                  <input className="form-control form-control-sm mb-1"
                         placeholder="Filter materials…"
                         value={materialSearch}
                         onChange={(e) => setMaterialSearch(e.target.value)} />
                  <select className="form-select form-select-sm"
                          value={form.material_id}
                          onChange={(e) => setForm({ ...form, material_id: e.target.value })}>
                    <option value="">— pick a material —</option>
                    {materials
                      .filter((m) => {
                        const q = materialSearch.trim().toLowerCase();
                        if (!q) return true;
                        return `${m.material_code || ""} ${m.description || ""}`.toLowerCase().includes(q);
                      })
                      .slice(0, 300)
                      .map((m) => (
                        <option key={m.material_id} value={m.material_id}>
                          {m.material_code ? `${m.material_code} — ` : ""}{m.description}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="col-md-4">
                <label className="form-label small mb-1">Pieces per stock length *</label>
                <input type="number" min="1" className="form-control form-control-sm"
                       value={form.pieces_per_stock_length}
                       onChange={(e) => setForm({ ...form, pieces_per_stock_length: e.target.value })} />
                <div className="form-text">
                  Finished pieces from ONE purchased stick. Receipts divide by this and round up.
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Piece length (in)</label>
                <input type="number" step="0.001" className="form-control form-control-sm"
                       value={form.piece_length}
                       onChange={(e) => setForm({ ...form, piece_length: e.target.value })} />
                <div className="form-text">Reference only — the count above is what converts.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Usable stock length (in)</label>
                <input type="number" step="0.001" className="form-control form-control-sm"
                       value={form.usable_stock_length}
                       onChange={(e) => setForm({ ...form, usable_stock_length: e.target.value })} />
                <div className="form-text">After the vendor's trim, if it differs from the stick.</div>
              </div>
              <div className="col-12">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="fiReturnsDrops"
                         checked={form.returns_drops}
                         onChange={(e) => setForm({ ...form, returns_drops: e.target.checked })} />
                  <label className="form-check-label" htmlFor="fiReturnsDrops">
                    Vendor returns the drops
                  </label>
                </div>
                <div className="form-text">
                  On when the offcut comes back to us. Each consumed stick then credits one
                  remnant at the length below. Leave off and the vendor keeps the drop — no
                  remnant, and the whole stick is charged to the component.
                </div>
              </div>
              {form.returns_drops && (
                <div className="col-md-4">
                  <label className="form-label small mb-1">Expected drop length (in)</label>
                  <input type="number" step="0.001" className="form-control form-control-sm"
                         value={form.expected_drop_length}
                         onChange={(e) => setForm({ ...form, expected_drop_length: e.target.value })} />
                </div>
              )}
              <div className="col-12">
                <label className="form-label small mb-1">Notes</label>
                <input className="form-control form-control-sm" value={form.notes}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="mt-3">
              <button className="btn btn-sm btn-success me-2" onClick={save} disabled={busy}>
                {busy ? "Saving…" : editingId ? "Save changes" : "Link material"}
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={cancel} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
